import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { RELEASES } from "../data/content";
import {
  SpotifyIcon,
  AppleMusicIcon,
  YouTubeIcon,
  SoundCloudIcon,
} from "./icons/PlatformIcons";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const PlatformLink = ({ href, label, testid, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    aria-label={label}
    title={label}
    className="w-9 h-9 grid place-items-center text-[#F5F5F0]/70 hover:text-[#C5A059] border border-white/10 hover:border-[#C5A059] rounded-full transition-all duration-300"
  >
    {children}
  </a>
);

const PlayIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
  </svg>
);

const PLATFORM_META = [
  { key: "spotify", name: "Spotify", Icon: SpotifyIcon },
  { key: "apple", name: "Apple Music", Icon: AppleMusicIcon },
  { key: "youtube", name: "YouTube", Icon: YouTubeIcon },
  { key: "soundcloud", name: "SoundCloud", Icon: SoundCloudIcon },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "Album", label: "Albums" },
  { key: "EP", label: "EPs" },
  { key: "Single", label: "Singles" },
];

export default function Music() {
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [activeRelease, setActiveRelease] = useState(null);
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(
    () => (filter === "all" ? RELEASES : RELEASES.filter((r) => r.type === filter)),
    [filter]
  );

  const counts = useMemo(
    () =>
      FILTERS.reduce((acc, f) => {
        acc[f.key] =
          f.key === "all"
            ? RELEASES.length
            : RELEASES.filter((r) => r.type === f.key).length;
        return acc;
      }, {}),
    []
  );

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  const togglePlay = (release, e) => {
    if (e) e.stopPropagation();
    if (!release.preview) return;
    const audio = audioRef.current;
    if (!audio) return;

    if (playingId === release.id) {
      audio.pause();
      setPlayingId(null);
      return;
    }

    setLoadingId(release.id);
    audio.src = release.preview;
    audio.currentTime = 0;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise
        .then(() => {
          setPlayingId(release.id);
          setLoadingId(null);
        })
        .catch(() => {
          setLoadingId(null);
          setPlayingId(null);
        });
    } else {
      setPlayingId(release.id);
      setLoadingId(null);
    }
  };

  const openRelease = (release) => setActiveRelease(release);
  const closeRelease = (open) => {
    if (!open) setActiveRelease(null);
  };

  return (
    <section
      id="music"
      data-testid="music-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <audio
        ref={audioRef}
        onEnded={() => setPlayingId(null)}
        onPause={() => {
          if (audioRef.current && audioRef.current.ended) setPlayingId(null);
        }}
        onError={() => {
          setLoadingId(null);
          setPlayingId(null);
        }}
        preload="none"
        crossOrigin="anonymous"
        data-testid="teaser-audio"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-8 md:mb-12 gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="font-display text-[#C5A059] text-lg md:text-xl tracking-tight">
                Ch. 01
              </span>
              <span className="h-px w-16 bg-[#C5A059]/40" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059]">
                Discography
              </span>
            </div>
            <h2 className="font-display uppercase font-semibold tracking-tighter text-[#F5F5F0] text-4xl md:text-6xl">
              Latest <span className="text-[#C5A059]">Releases</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm text-[#F5F5F0]/60 leading-relaxed">
            Click any cover to see every platform this release lives on, or hit
            the gold play button for a 30-second teaser.
          </p>
        </div>

        {/* Filter tabs */}
        <div
          data-testid="music-filters"
          className="mb-10 md:mb-14 flex flex-wrap items-center gap-2"
        >
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                data-testid={`music-filter-${f.key}`}
                className={`group inline-flex items-center gap-2 px-4 py-2 border text-[10px] uppercase tracking-[0.3em] transition-all duration-300 ${
                  active
                    ? "border-[#C5A059] bg-[#C5A059] text-[#050505]"
                    : "border-white/10 text-[#F5F5F0]/70 hover:text-[#C5A059] hover:border-[#C5A059]/60"
                }`}
              >
                {f.label}
                <span
                  className={`tabular-nums text-[9px] ${
                    active ? "text-[#050505]/60" : "text-[#F5F5F0]/40"
                  }`}
                >
                  {String(counts[f.key]).padStart(2, "0")}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filtered.map((r, i) => {
            const isPlaying = playingId === r.id;
            const isLoading = loadingId === r.id;
            return (
              <motion.article
                key={r.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  delay: (i % 6) * 0.06,
                  ease: [0.22, 1, 0.36, 1],
                }}
                data-testid={`release-card-${r.id}`}
                className="group relative bg-[#0c0c0c] border border-white/5 hover:border-[#C5A059]/60 transition-colors duration-500"
              >
                <button
                  type="button"
                  onClick={() => openRelease(r)}
                  data-testid={`release-open-${r.id}`}
                  aria-label={`See where to listen to ${r.title}`}
                  className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-[#C5A059]/60"
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={r.cover}
                      alt={r.title}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-70" />
                    <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] text-[#C5A059] border border-[#C5A059]/40 px-2 py-1 bg-[#050505]/40 backdrop-blur-sm">
                      {r.type} · {r.year}
                    </div>

                    {/* Hover hint */}
                    <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <span className="text-[10px] uppercase tracking-[0.4em] text-[#F5F5F0] bg-[#050505]/70 border border-[#C5A059]/60 px-4 py-2 backdrop-blur-md">
                        View Streaming Links
                      </span>
                    </div>

                    {/* Play teaser */}
                    {r.preview && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => togglePlay(r, e)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            togglePlay(r, e);
                          }
                        }}
                        data-testid={`play-teaser-${r.id}`}
                        aria-label={isPlaying ? `Pause teaser of ${r.title}` : `Play teaser of ${r.title}`}
                        className={`absolute bottom-4 right-4 w-14 h-14 grid place-items-center rounded-full border transition-all duration-300 backdrop-blur-md cursor-pointer ${
                          isPlaying
                            ? "bg-[#C5A059] text-[#050505] border-[#C5A059] scale-100"
                            : "bg-[#050505]/60 text-[#C5A059] border-[#C5A059]/60 hover:bg-[#C5A059] hover:text-[#050505] hover:scale-105"
                        }`}
                      >
                        {isLoading ? (
                          <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : isPlaying ? (
                          <PauseIcon />
                        ) : (
                          <PlayIcon />
                        )}
                      </span>
                    )}

                    {isPlaying && (
                      <div className="absolute bottom-5 left-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                        <span className="flex items-end gap-[2px] h-3">
                          <span className="w-[2px] bg-[#C5A059] animate-pulse h-2" />
                          <span className="w-[2px] bg-[#C5A059] animate-pulse h-3" style={{ animationDelay: "120ms" }} />
                          <span className="w-[2px] bg-[#C5A059] animate-pulse h-1.5" style={{ animationDelay: "240ms" }} />
                          <span className="w-[2px] bg-[#C5A059] animate-pulse h-2.5" style={{ animationDelay: "60ms" }} />
                        </span>
                        Playing
                      </div>
                    )}
                  </div>

                  <div className="p-6 flex items-center justify-between gap-4">
                    <h3 className="font-display text-xl md:text-2xl text-[#F5F5F0] tracking-tight">
                      {r.title}
                    </h3>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <PlatformLink href={r.links.spotify} label="Spotify" testid={`music-link-spotify-${r.id}`}>
                        <SpotifyIcon />
                      </PlatformLink>
                      <PlatformLink href={r.links.apple} label="Apple Music" testid={`music-link-apple-${r.id}`}>
                        <AppleMusicIcon />
                      </PlatformLink>
                      <PlatformLink href={r.links.youtube} label="YouTube" testid={`music-link-youtube-${r.id}`}>
                        <YouTubeIcon />
                      </PlatformLink>
                      <PlatformLink href={r.links.soundcloud} label="SoundCloud" testid={`music-link-soundcloud-${r.id}`}>
                        <SoundCloudIcon />
                      </PlatformLink>
                    </div>
                  </div>
                </button>
              </motion.article>
            );
          })}
        </div>
      </div>

      {/* Release detail dialog */}
      <Dialog open={!!activeRelease} onOpenChange={closeRelease}>
        <DialogContent
          data-testid="release-dialog"
          className="max-w-3xl w-[92vw] p-0 border border-[#C5A059]/40 bg-[#0a0a0a] text-[#F5F5F0] overflow-hidden"
        >
          {activeRelease && (
            <div className="grid grid-cols-1 md:grid-cols-5">
              <div className="md:col-span-2 relative aspect-square md:aspect-auto">
                <img
                  src={activeRelease.cover}
                  alt={activeRelease.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 md:bg-gradient-to-r md:from-transparent md:to-[#0a0a0a]" />
              </div>

              <div className="md:col-span-3 p-6 md:p-8 flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059]">
                  {activeRelease.type} · {activeRelease.year}
                </span>
                <DialogTitle asChild>
                  <h3 className="mt-2 font-display uppercase font-bold tracking-tighter text-[#F5F5F0] text-3xl md:text-4xl leading-[0.95]">
                    {activeRelease.title}
                  </h3>
                </DialogTitle>
                <DialogDescription asChild>
                  <p className="mt-3 text-sm text-[#F5F5F0]/60 leading-relaxed">
                    Available everywhere. Pick your platform below to stream,
                    download or add to your library.
                  </p>
                </DialogDescription>

                {activeRelease.preview && (
                  <button
                    type="button"
                    onClick={(e) => togglePlay(activeRelease, e)}
                    data-testid="dialog-play-teaser"
                    className={`mt-6 self-start inline-flex items-center gap-3 px-5 py-3 border text-xs uppercase tracking-[0.3em] transition-all duration-300 ${
                      playingId === activeRelease.id
                        ? "bg-[#C5A059] text-[#050505] border-[#C5A059]"
                        : "text-[#C5A059] border-[#C5A059]/60 hover:bg-[#C5A059] hover:text-[#050505]"
                    }`}
                  >
                    {playingId === activeRelease.id ? <PauseIcon /> : <PlayIcon />}
                    {playingId === activeRelease.id ? "Pause Teaser" : "Play 30s Teaser"}
                  </button>
                )}

                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {PLATFORM_META.map(({ key, name, Icon }) => (
                    <a
                      key={key}
                      href={activeRelease.links[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`dialog-platform-${key}`}
                      className="group flex items-center gap-3 border border-white/10 hover:border-[#C5A059] px-4 py-3 bg-[#0f0f0f] hover:bg-[#141210] transition-all duration-300"
                    >
                      <span className="shrink-0 w-9 h-9 grid place-items-center border border-white/10 group-hover:border-[#C5A059] group-hover:text-[#C5A059] text-[#F5F5F0]/70 transition-colors">
                        <Icon />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[9px] uppercase tracking-[0.25em] text-[#F5F5F0]/40">
                          Listen on
                        </span>
                        <span className="block font-display text-base text-[#F5F5F0] group-hover:text-[#C5A059] transition-colors whitespace-nowrap">
                          {name}
                        </span>
                      </span>
                      <span className="shrink-0 text-[#F5F5F0]/30 group-hover:text-[#C5A059] transition-all group-hover:translate-x-0.5">
                        →
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
